/**
 * Point (Discover Pakistan) detail page.
 * Fetches point by slug from points API, displays key info and comments/reviews.
 * Users must be logged in to add a comment (same pattern as Hotel and Post detail).
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import PageLayout from '../../components/layout/PageLayout';
import pointService from '../../services/pointService';
import { useAuth } from '../../contexts/AuthContext';
import { StarIcon } from '@heroicons/react/24/solid';
import { getSlugFromParams } from '../../utils/urlHelpers';
import { formatDate } from '../../utils/helpers';
import GoogleMapsLocationLink from '../../components/common/GoogleMapsLocationLink';
import { hasGoogleMapsTarget } from '../../utils/googleMaps';
import useImageLightbox from '../../hooks/useImageLightbox';
import { normalizeGalleryImages } from '../../utils/imageLightbox';
import {
  IMAGE_PLACEHOLDER_PATH,
  onPointImageError,
} from './pointImageUtils';
import {
  withImagePlaceholder,
  imageFieldUrl,
} from '../../utils/imageFallback';
import { canManageOwnedResource } from '../../utils/resourcePermissions';

const PointDetailPage = () => {
  const params = useParams();
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const pointSlug = getSlugFromParams(params, 'point');
  const [point, setPoint] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [showReviewForm, setShowReviewForm] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, title: '', comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const { openLightbox, Lightbox } = useImageLightbox();

  useEffect(() => {
    if (pointSlug) loadPoint();
  }, [pointSlug]);

  useEffect(() => {
    if (point?.slug) loadReviews();
  }, [point?.slug]);

  const loadPoint = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await pointService.getPoint(pointSlug);
      setPoint(data);
    } catch (err) {
      console.error('Error loading point:', err);
      setError('Failed to load place details');
    } finally {
      setLoading(false);
    }
  };

  const loadReviews = async () => {
    if (!point?.slug) return;
    try {
      const response = await pointService.getPointReviews(point.slug);
      setReviews(response.results || response || []);
    } catch (err) {
      console.error('Error loading reviews:', err);
      setReviews([]);
    }
  };

  const handleReviewSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/points/${pointSlug}` } });
      return;
    }
    if (!point?.slug) return;
    setSubmittingReview(true);
    try {
      await pointService.createPointReview(point.slug, reviewForm);
      await loadReviews();
      await loadPoint();
      setReviewForm({ rating: 5, title: '', comment: '' });
      setShowReviewForm(false);
    } catch (err) {
      console.error('Error submitting review:', err);
      if (err.response?.status === 401) {
        navigate('/login', { state: { from: `/points/${pointSlug}` } });
      } else {
        alert('Failed to submit comment. Please try again.');
      }
    } finally {
      setSubmittingReview(false);
    }
  };

  const renderStars = (count) =>
    [...Array(5)].map((_, i) => (
      <StarIcon
        key={i}
        className={`h-5 w-5 ${i < count ? 'text-yellow-400 fill-current' : 'text-gray-300'}`}
      />
    ));

  const uploadedImages = useMemo(
    () => (Array.isArray(point?.images) ? point.images : []),
    [point?.images],
  );

  const gallerySlides = useMemo(() => {
    const slides = normalizeGalleryImages(uploadedImages, point?.name || 'Point');
    if (slides.length > 0) return slides;
    const fallback = withImagePlaceholder(
      imageFieldUrl(point?.primary_image_url) ||
        imageFieldUrl(point?.cover_image_url),
    );
    return [{ id: 'placeholder', url: fallback, alt: point?.name || 'Point' }];
  }, [uploadedImages, point?.name, point?.primary_image_url, point?.cover_image_url]);

  const lightboxSlides = useMemo(
    () => normalizeGalleryImages(uploadedImages, point?.name || 'Point'),
    [uploadedImages, point?.name],
  );

  const heroUrl =
    gallerySlides[Math.min(currentImageIndex, gallerySlides.length - 1)]?.url ||
    IMAGE_PLACEHOLDER_PATH;

  const formatReviewCount = (count) => {
    if (!count || count === 0) return '0 reviews';
    if (count >= 1000) return `${(count / 1000).toFixed(1)}K reviews`;
    return `${count} review${count !== 1 ? 's' : ''}`;
  };

  if (loading) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto" />
            <p className="mt-4 text-gray-600">Loading place details...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  const canManage = isAuthenticated && user && point && canManageOwnedResource(user, point);

  if (error || !point) {
    return (
      <PageLayout title="PakPoints">
        <div className="min-h-screen flex items-center justify-center">
          <div className="text-center">
            <p className="text-gray-600">{error || 'Place not found'}</p>
            <button
              type="button"
              onClick={() => navigate('/points')}
              className="mt-4 text-blue-600 hover:underline"
            >
              Back to Points
            </button>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout title={point.name}>
      <div className="max-w-4xl mx-auto px-4 py-6">
        <button
          type="button"
          onClick={() => navigate('/points')}
          className="text-gray-600 hover:text-gray-900 mb-4"
        >
          ← Back to Points
        </button>

        <div className="flex flex-wrap items-start justify-between gap-3 mb-2">
          <h1 className="text-2xl font-bold text-gray-900">{point.name}</h1>
          {canManage && (
            <button
              type="button"
              onClick={() => navigate(`/points/${point.slug}/edit`)}
              className="shrink-0 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 hover:bg-gray-50"
            >
              Edit
            </button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2 text-sm text-gray-600 mb-4">
          {point.point_category?.name && (
            <span className="bg-gray-100 px-2 py-0.5 rounded">{point.point_category.name}</span>
          )}
          {point.average_rating != null && (
            <span className="flex items-center gap-1">
              <StarIcon className="h-4 w-4 text-yellow-500 fill-current" />
              {Number(point.average_rating).toFixed(1)}
            </span>
          )}
          {point.total_reviews != null && (
            <span>{formatReviewCount(point.total_reviews)}</span>
          )}
          {point.is_free && <span className="text-green-600 font-medium">Free entry</span>}
          {point.best_visit_months && (
            <span>Best time: {point.best_visit_months}</span>
          )}
        </div>

        <div className="rounded-lg overflow-hidden bg-gray-100 border border-gray-200 mb-6">
          <button
            type="button"
            onClick={() => {
              if (lightboxSlides.length > 0) {
                openLightbox(lightboxSlides, currentImageIndex);
              }
            }}
            className="block w-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-600 disabled:cursor-default"
            aria-label={lightboxSlides.length > 0 ? 'View full size image' : point.name}
            disabled={lightboxSlides.length === 0}
          >
            <img
              src={heroUrl}
              alt={point.name}
              className="w-full h-64 sm:h-96 object-cover bg-gray-100"
              onError={onPointImageError}
            />
          </button>
          {lightboxSlides.length > 1 && (
            <div className="flex gap-2 p-3 overflow-x-auto border-t border-gray-200 bg-white">
              {lightboxSlides.map((slide, idx) => (
                <button
                  key={slide.id}
                  type="button"
                  onClick={() => {
                    setCurrentImageIndex(idx);
                    openLightbox(lightboxSlides, idx);
                  }}
                  className={`flex-shrink-0 w-16 h-16 sm:w-20 sm:h-20 rounded-lg overflow-hidden border-2 transition-colors ${
                    currentImageIndex === idx ? 'border-blue-600' : 'border-gray-200 hover:border-blue-400'
                  }`}
                  aria-label={`View image ${idx + 1}`}
                >
                  <img
                    src={slide.url}
                    alt=""
                    className="w-full h-full object-cover"
                    onError={onPointImageError}
                  />
                </button>
              ))}
            </div>
          )}
        </div>
        {Lightbox}

        {hasGoogleMapsTarget({
          latitude: point.latitude,
          longitude: point.longitude,
          address: point.address,
          city: point.city,
          name: point.name,
        }) && (
          <div className="mb-4 space-y-2">
            {(point.city || point.area || point.address) && (
              <p className="text-gray-600">
                {[point.area, point.city, point.address].filter(Boolean).join(' · ')}
              </p>
            )}
            <GoogleMapsLocationLink
              latitude={point.latitude}
              longitude={point.longitude}
              address={point.address}
              city={point.city}
              area={point.area}
              name={point.name}
              label="View on Map"
            />
          </div>
        )}

        {point.short_description && (
          <p className="text-gray-700 font-medium mb-2">{point.short_description}</p>
        )}

        {point.description && (
          <div className="prose prose-gray max-w-none mb-6">
            <p className="whitespace-pre-wrap">{point.description}</p>
          </div>
        )}

        {/* Comments / Reviews section */}
        <section className="mt-8 pt-8 border-t border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Comments ({reviews.length})
            </h2>
            {isAuthenticated && user && (
              <button
                type="button"
                onClick={() => setShowReviewForm(!showReviewForm)}
                className="px-4 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors"
              >
                {showReviewForm ? 'Cancel' : 'Add comment'}
              </button>
            )}
          </div>

          {showReviewForm && user && (
            <div className="mb-6 border border-gray-200 rounded-lg p-4 bg-gray-50">
              <form onSubmit={handleReviewSubmit}>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                  <div className="flex items-center gap-2">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <button
                        key={star}
                        type="button"
                        onClick={() => setReviewForm({ ...reviewForm, rating: star })}
                        className="focus:outline-none"
                      >
                        <StarIcon
                          className={`h-6 w-6 ${
                            star <= reviewForm.rating ? 'text-yellow-400 fill-current' : 'text-gray-300'
                          }`}
                        />
                      </button>
                    ))}
                    <span className="ml-2 text-sm text-gray-600">{reviewForm.rating} out of 5</span>
                  </div>
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Title (optional)</label>
                  <input
                    type="text"
                    value={reviewForm.title}
                    onChange={(e) => setReviewForm({ ...reviewForm, title: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Give your review a title"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Comment</label>
                  <textarea
                    value={reviewForm.comment}
                    onChange={(e) => setReviewForm({ ...reviewForm, comment: e.target.value })}
                    rows={4}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
                    placeholder="Share your experience..."
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={submittingReview}
                    className="px-6 py-2 bg-primary-600 text-white text-sm font-medium rounded-lg hover:bg-primary-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {submittingReview ? 'Submitting...' : 'Submit comment'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {!isAuthenticated && (
            <div className="mb-4 p-3 bg-primary-50 border border-primary-200 rounded-lg">
              <p className="text-sm text-primary-800">
                Please <Link to="/login" state={{ from: `/points/${pointSlug}` }} className="underline font-medium">log in</Link> to add a comment.
              </p>
            </div>
          )}

          {reviews.length > 0 ? (
            <div className="space-y-4">
              {reviews.map((review) => (
                <div key={review.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <h4 className="font-semibold text-gray-900">{review.user_name || 'Anonymous'}</h4>
                      <div className="flex items-center gap-1 mt-1">{renderStars(review.rating)}</div>
                    </div>
                    <span className="text-sm text-gray-500">{formatDate(review.created_at)}</span>
                  </div>
                  {review.title && <h5 className="font-medium text-gray-800 mb-1">{review.title}</h5>}
                  {review.comment && <p className="text-gray-700 text-sm">{review.comment}</p>}
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center py-6 text-gray-500">No comments yet. Be the first to share your experience!</p>
          )}
        </section>

        {point.views != null && point.views > 0 && (
          <p className="text-sm text-gray-500 mt-4">{point.views} views</p>
        )}
      </div>
    </PageLayout>
  );
};

export default PointDetailPage;
